import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Alert, CircularProgress, Typography, Box,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import type { Study, OrderInfo } from '../../api/types';
import { api } from '../../api/rpc';

interface AuthOrderDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

export const AuthOrderDialog = ({ open, study, onClose, onComplete }: AuthOrderDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);

  // Auth fields stored in detailOrder extension fields
  const [authStatus, setAuthStatus] = useState('PENDING');
  const [authNumber, setAuthNumber] = useState('');
  const [authExpiration, setAuthExpiration] = useState('');
  const [authNotes, setAuthNotes] = useState('');
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [insurancePlan, setInsurancePlan] = useState('');

  useEffect(() => {
    if (!open || !study) return;
    setLoading(true);
    setError(null);

    api.getOrder(study.objKey)
      .then(oi => {
        setOrder(oi);
        if (oi?.detailOrder) {
          setAuthStatus((oi.detailOrder.authStatus as string) ?? 'PENDING');
          setAuthNumber((oi.detailOrder.authNumber as string) ?? '');
          setAuthExpiration((oi.detailOrder.authExpiration as string) ?? '');
          setAuthNotes((oi.detailOrder.authNotes as string) ?? '');
          setInsuranceCompany((oi.detailOrder.insuranceCompany as string) ?? '');
          setInsurancePlan((oi.detailOrder.insurancePlan as string) ?? '');
        }
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load order'))
      .finally(() => setLoading(false));
  }, [open, study]);

  const handleSave = async () => {
    if (!study || !order) return;
    setSaving(true);
    setError(null);
    try {
      const updatedOrder: OrderInfo = {
        ...order,
        detailOrder: {
          ...order.detailOrder,
          authStatus,
          authNumber,
          authExpiration,
          authNotes,
          insuranceCompany,
          insurancePlan,
        },
      };
      await api.updateInsurances(study.objKey, updatedOrder, study.patientKey);
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save authorization');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Order Authorization</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{study.name}</strong> — {study.acc} — {study.mod}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Auth Status</InputLabel>
                <Select
                  value={authStatus}
                  label="Auth Status"
                  onChange={e => setAuthStatus(e.target.value)}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="DENIED">Denied</MenuItem>
                  <MenuItem value="PARTIAL">Partial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Auth Number"
                value={authNumber}
                onChange={e => setAuthNumber(e.target.value)}
                fullWidth size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Insurance Company"
                value={insuranceCompany}
                onChange={e => setInsuranceCompany(e.target.value)}
                fullWidth size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Insurance Plan"
                value={insurancePlan}
                onChange={e => setInsurancePlan(e.target.value)}
                fullWidth size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Auth Expiration"
                value={authExpiration}
                onChange={e => setAuthExpiration(e.target.value)}
                fullWidth size="small"
                placeholder="MM/DD/YYYY"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes"
                value={authNotes}
                onChange={e => setAuthNotes(e.target.value)}
                fullWidth multiline rows={3} size="small"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
